/**
 * Teen Affirmations Page
 * Categories of empowering affirmations with voice playback functionality.
 * Uses TeenThemeContext for customizable look and feel.
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Star,
  Target,
  Heart,
  Users,
  BookOpen,
  Flame,
  Play,
  Pause,
  SkipForward,
  Volume2,
  X,
  Shuffle,
  Mic,
  User,
  Loader2
} from "lucide-react";
import axios from "axios";
import AudioWaveform from "../../components/AudioWaveform";
import { TeenThemeProvider, useTeenTheme } from "../../contexts/TeenThemeContext";
import { voiceVolume, getStoredAudioMode } from "../../utils/modeAudio";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const ICONS = {
  star: Star,
  target: Target,
  heart: Heart,
  users: Users,
  book: BookOpen,
  flame: Flame
};

const TeenAffirmationsContent = () => {
  const navigate = useNavigate();
  const { theme, currentPalette } = useTeenTheme();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [shuffled, setShuffled] = useState(false);
  const [displayAffirmations, setDisplayAffirmations] = useState([]);
  const [voiceType, setVoiceType] = useState("narrator"); // "narrator" or "user"
  const [audioData, setAudioData] = useState([]);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  
  // Theme colors
  const colors = {
    base: theme?.colors?.base || '#0f0f23',
    primary: currentPalette?.primary || theme?.colors?.primary || '#818CF8',
    accent: currentPalette?.accent || theme?.colors?.accent || '#F472B6',
    textPrimary: theme?.colors?.textPrimary || '#ffffff',
    textSecondary: theme?.colors?.textSecondary || 'rgba(255,255,255,0.6)',
  };
  
  const audioRef = useRef(null);
  const playingRef = useRef(false);
  const getToken = () => localStorage.getItem("reclaim_token");

  useEffect(() => {
    loadCategories();
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, []);

  const loadCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/teen/affirmations`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setCategories(res.data.categories);
    } catch (err) {
      console.error("Error loading categories:", err);
    }
  };

  const handleSelectCategory = async (category) => {
    setSelectedCategory(category);
    setDisplayAffirmations([...category.affirmations]);
    setCurrentIndex(0);
    setIsPlaying(false);
    setShuffled(false);
    setAudioData([]);
    
    // Load audio for this category
    loadCategoryAudio(category.id);
  };

  const loadCategoryAudio = async (categoryId) => {
    setIsLoadingAudio(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/teen/affirmations/${categoryId}/audio?voice_type=${voiceType}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      if (res.data.audio_available) {
        setAudioData(res.data.audio_data);
      }
    } catch (err) {
      console.error("Error loading audio:", err);
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const handleShuffle = () => {
    // Shuffle both display affirmations and audio data together
    const combined = displayAffirmations.map((aff, idx) => ({
      text: aff,
      audio: audioData[idx]
    }));
    const shuffledCombined = [...combined].sort(() => Math.random() - 0.5);
    
    setDisplayAffirmations(shuffledCombined.map(c => c.text));
    setAudioData(shuffledCombined.map(c => c.audio).filter(Boolean));
    setShuffled(true);
    setCurrentIndex(0);
  };

  const playCurrentAffirmation = (index) => {
    // Find audio for current affirmation
    const currentText = displayAffirmations[index];
    const audioItem = audioData.find(a => a?.text === currentText);
    
    if (audioItem?.audio_url) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      const audio = new Audio(audioItem.audio_url);
      audio.volume = voiceVolume(getStoredAudioMode());
      audioRef.current = audio;
      
      audio.onplay = () => setAudioPlaying(true);
      audio.onended = () => {
        setAudioPlaying(false);
        // Auto-advance if playing
        if (playingRef.current) {
          const nextIndex = (index + 1) % displayAffirmations.length;
          if (nextIndex === 0) {
            // Looped back to start - stop
            setIsPlaying(false);
            playingRef.current = false;
          } else {
            setCurrentIndex(nextIndex);
            setTimeout(() => playCurrentAffirmation(nextIndex), 500);
          }
        }
      };
      audio.onerror = () => {
        setAudioPlaying(false);
        // Continue anyway after delay
        if (playingRef.current) {
          setTimeout(() => {
            const nextIndex = (index + 1) % displayAffirmations.length;
            setCurrentIndex(nextIndex);
            playCurrentAffirmation(nextIndex);
          }, 3000);
        }
      };
      
      audio.play().catch(err => {
        console.error("Audio play error:", err);
        setAudioPlaying(false);
      });
    } else {
      // No audio - use timer
      if (playingRef.current) {
        setTimeout(() => {
          const nextIndex = (index + 1) % displayAffirmations.length;
          if (nextIndex === 0) {
            setIsPlaying(false);
            playingRef.current = false;
          } else {
            setCurrentIndex(nextIndex);
            playCurrentAffirmation(nextIndex);
          }
        }, 4000);
      }
    }
  };

  const handlePlayPause = () => {
    if (isPlaying) {
      if (audioRef.current) audioRef.current.pause();
      setIsPlaying(false);
      playingRef.current = false;
      setAudioPlaying(false);
    } else {
      setIsPlaying(true);
      playingRef.current = true;
      playCurrentAffirmation(currentIndex);
    }
  };

  const handleNext = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    setAudioPlaying(false);
    
    const nextIndex = (currentIndex + 1) % displayAffirmations.length;
    setCurrentIndex(nextIndex);
    
    // Play next if in playing mode
    if (isPlaying) {
      setTimeout(() => playCurrentAffirmation(nextIndex), 300);
    }
  };

  const handleClose = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    setSelectedCategory(null);
    setIsPlaying(false);
    playingRef.current = false;
    setAudioPlaying(false);
    setAudioData([]);
  };

  const toggleVoiceType = () => {
    const newType = voiceType === "narrator" ? "user" : "narrator";
    setVoiceType(newType);
    if (selectedCategory) {
      setAudioData([]);
      loadCategoryAudio(selectedCategory.id);
    }
  };

  // Category grid
  if (!selectedCategory) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f0f23] via-[#1a1a3e] to-[#0f0f23]">
        {/* Header */}
        <div className="p-4">
          <button
            onClick={() => navigate("/teen")}
            className="text-white/60 hover:text-white flex items-center gap-2"
          >
            <ArrowLeft size={20} />
          </button>
        </div>

        {/* Title */}
        <div className="px-6 pt-2 pb-6">
          <h1 className="text-2xl font-bold text-white mb-2">Power Affirmations</h1>
          <p className="text-white/60">Choose a category to start</p>
        </div>

        {/* Category Grid */}
        <div className="px-6 pb-8 grid grid-cols-2 gap-4">
          {categories.map((category, index) => {
            const Icon = ICONS[category.icon] || Star;
            return (
              <motion.button
                key={category.id}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleSelectCategory(category)}
                className={`p-5 rounded-2xl bg-gradient-to-br ${category.color} shadow-xl text-left`}
                data-testid={`affirmation-category-${category.id}`}
              >
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center mb-3">
                  <Icon size={24} className="text-white" />
                </div>
                <h3 className="text-white font-bold mb-1">{category.name}</h3>
                <p className="text-white/80 text-xs">{category.description}</p>
                <div className="mt-3 flex items-center gap-2 text-white/60 text-xs">
                  <Volume2 size={12} />
                  <span>{category.affirmations.length} with voice</span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    );
  }

  // Affirmation player
  const Icon = ICONS[selectedCategory.icon] || Star;
  const currentAffirmation = displayAffirmations[currentIndex];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className={`min-h-screen bg-gradient-to-b ${selectedCategory.color} flex flex-col`}
    >
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <button
          onClick={handleClose}
          className="text-white/80 hover:text-white"
        >
          <X size={24} />
        </button>
        <span className="text-white/80 text-sm">
          {currentIndex + 1} / {displayAffirmations.length}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleVoiceType}
            className={`p-2 rounded-lg ${voiceType === "user" ? 'bg-white/30' : 'bg-white/10'}`}
            title={voiceType === "user" ? "Using your voice" : "Using narrator voice"}
          >
            {voiceType === "user" ? (
              <Mic size={18} className="text-white" />
            ) : (
              <User size={18} className="text-white" />
            )}
          </button>
          <button
            onClick={handleShuffle}
            className={`p-2 rounded-lg ${shuffled ? 'bg-white/30' : 'bg-white/10'}`}
          >
            <Shuffle size={18} className="text-white" />
          </button>
        </div>
      </div>

      {/* Loading indicator */}
      {isLoadingAudio && (
        <div className="flex items-center justify-center gap-2 py-2">
          <Loader2 size={16} className="text-white/60 animate-spin" />
          <span className="text-white/60 text-sm">Loading voice...</span>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 pb-8">
        {/* Icon with audio indicator */}
        <motion.div
          key={currentIndex}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="relative w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center mb-8"
        >
          <Icon size={36} className="text-white" />
          {audioPlaying && (
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">
              <AudioWaveform isPlaying={true} barCount={5} color="white" size="sm" />
            </div>
          )}
        </motion.div>

        {/* Affirmation Text */}
        <AnimatePresence mode="wait">
          <motion.p
            key={currentIndex}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -20, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="text-white text-2xl font-medium text-center leading-relaxed max-w-md"
          >
            {currentAffirmation}
          </motion.p>
        </AnimatePresence>

        {/* Progress Dots */}
        <div className="flex gap-1.5 mt-8 flex-wrap justify-center max-w-xs">
          {displayAffirmations.map((_, idx) => (
            <div
              key={idx}
              className={`h-1.5 rounded-full transition-all ${
                idx === currentIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/30'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="p-6 flex items-center justify-center gap-6">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handlePlayPause}
          className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center"
          data-testid="play-pause-btn"
        >
          {isPlaying ? (
            <Pause size={28} className="text-white" />
          ) : (
            <Play size={28} className="text-white ml-1" />
          )}
        </motion.button>
        
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleNext}
          className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center"
        >
          <SkipForward size={22} className="text-white" />
        </motion.button>
      </div>

      {/* Voice indicator */}
      <div className="pb-8 text-center">
        <span className="text-white/60 text-sm flex items-center justify-center gap-2">
          {voiceType === "user" ? <Mic size={14} /> : <User size={14} />}
          {voiceType === "user" ? "Your voice" : "Narrator voice"}
        </span>
      </div>
    </motion.div>
  );
};

// Main component wraps with theme provider
const TeenAffirmationsPage = () => {
  return (
    <TeenThemeProvider>
      <TeenAffirmationsContent />
    </TeenThemeProvider>
  );
};

export default TeenAffirmationsPage;
